// Agent Editor Component
// View and edit AI agents with trigger configuration and actions

import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api/client'
import {
  Bot,
  ChevronLeft,
  AlertCircle,
  Loader2,
  Play,
  Pause,
  Clock,
  Zap,
  Calendar,
  Bell,
  Search,
  MessageSquare,
  Settings2,
  FileText,
  Users,
  AlertTriangle,
  Lightbulb,
  Briefcase,
  Sparkles,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface AgentAction {
  type: 'execute_prompt' | 'call_api' | 'create_entity' | 'notify'
  promptId?: string
  config?: Record<string, unknown>
}

interface ScheduleConfig {
  cron?: string
  timezone?: string
  intervalMinutes?: number
}

interface AIAgent {
  id: string
  key: string
  name: string
  description: string | null
  icon: string
  color: string | null
  triggerType: string
  triggerCondition: Record<string, unknown> | null
  scheduleConfig: ScheduleConfig | null
  actions: AgentAction[]
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

interface AgentEditorProps {
  agentKey: string
  onClose: () => void
}

// ============================================================================
// ICON HELPER
// ============================================================================

function getIcon(iconName: string, className: string = 'w-4 h-4') {
  const icons: Record<string, React.ReactNode> = {
    compass: <Sparkles className={className} />,
    'graduation-cap': <Sparkles className={className} />,
    'file-text': <FileText className={className} />,
    users: <Users className={className} />,
    'alert-triangle': <AlertTriangle className={className} />,
    sparkles: <Sparkles className={className} />,
    lightbulb: <Lightbulb className={className} />,
    briefcase: <Briefcase className={className} />,
    'message-square': <MessageSquare className={className} />,
    'book-open': <FileText className={className} />,
    'message-circle': <MessageSquare className={className} />,
    'bar-chart-2': <Settings2 className={className} />,
    bot: <Bot className={className} />,
    calendar: <Calendar className={className} />,
    search: <Search className={className} />,
  }
  return icons[iconName] || <Bot className={className} />
}

// ============================================================================
// DATA FETCHING
// ============================================================================

async function fetchAgent(key: string): Promise<AIAgent> {
  const response = await apiClient.get<{ agent: AIAgent }>(`/api/agents/${key}`)
  return response.data.agent
}

// ============================================================================
// TRIGGER TYPE CONFIG
// ============================================================================

const TRIGGER_TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; description: string }> = {
  on_enter: {
    label: 'On Phase Enter',
    icon: <Zap className="w-4 h-4" />,
    description: 'Triggers when user enters a specific phase',
  },
  on_action: {
    label: 'On Action',
    icon: <Play className="w-4 h-4" />,
    description: 'Triggers when a specific action is performed',
  },
  on_complete: {
    label: 'On Complete',
    icon: <MessageSquare className="w-4 h-4" />,
    description: 'Triggers when a task or workflow completes',
  },
  on_schedule: {
    label: 'Scheduled',
    icon: <Calendar className="w-4 h-4" />,
    description: 'Runs on a recurring schedule',
  },
  on_event: {
    label: 'On Event',
    icon: <Bell className="w-4 h-4" />,
    description: 'Triggers on specific system events',
  },
  on_request: {
    label: 'On Request',
    icon: <Search className="w-4 h-4" />,
    description: 'Triggered manually via API request',
  },
}

const ACTION_TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  execute_prompt: { label: 'Execute Prompt', icon: <MessageSquare className="w-4 h-4" /> },
  call_api: { label: 'Call API', icon: <Zap className="w-4 h-4" /> },
  create_entity: { label: 'Create Entity', icon: <FileText className="w-4 h-4" /> },
  notify: { label: 'Send Notification', icon: <Bell className="w-4 h-4" /> },
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AgentEditor({ agentKey, onClose }: AgentEditorProps) {
  const {
    data: agent,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ai-agent', agentKey],
    queryFn: () => fetchAgent(agentKey),
  })

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </CardContent>
      </Card>
    )
  }

  if (error || !agent) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center gap-2 text-solution py-8">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load agent</span>
        </CardContent>
      </Card>
    )
  }

  const triggerConfig = TRIGGER_TYPE_CONFIG[agent.triggerType] || {
    label: agent.triggerType,
    icon: <Zap className="w-4 h-4" />,
    description: 'Unknown trigger type',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onClose}>
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: agent.color ? `${agent.color}20` : 'var(--color-business)/10' }}
              >
                <span style={{ color: agent.color || 'var(--color-business)' }}>
                  {getIcon(agent.icon, 'w-5 h-5')}
                </span>
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{agent.name}</CardTitle>
                <CardDescription className="text-text-muted">
                  {agent.description || 'No description'}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{agent.key}</Badge>
              {agent.isSystem && (
                <Badge variant="outline" className="text-xs">
                  System
                </Badge>
              )}
              <Badge
                className={
                  agent.isActive
                    ? 'bg-market/10 text-market border border-market/20'
                    : 'bg-warm-100 text-text-muted'
                }
              >
                {agent.isActive ? (
                  <>
                    <Play className="w-3 h-3 mr-1" />
                    Running
                  </>
                ) : (
                  <>
                    <Pause className="w-3 h-3 mr-1" />
                    Paused
                  </>
                )}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Trigger Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Trigger Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-warm-50 rounded-lg border border-border-subtle">
            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--color-business)/10' }}
              >
                <span className="text-business">{triggerConfig.icon}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-text">{triggerConfig.label}</span>
                  <Badge variant="outline" className="text-xs font-mono">
                    {agent.triggerType}
                  </Badge>
                </div>
                <p className="text-sm text-text-muted mb-3">{triggerConfig.description}</p>

                {/* Schedule Config */}
                {agent.scheduleConfig && (
                  <div className="mt-3 p-3 bg-white rounded-md border border-warm-200">
                    <p className="text-xs font-medium text-text-secondary mb-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Schedule
                    </p>
                    <div className="space-y-1 text-sm">
                      {agent.scheduleConfig.cron && (
                        <p className="font-mono text-xs text-text-muted">
                          Cron: {agent.scheduleConfig.cron}
                        </p>
                      )}
                      {agent.scheduleConfig.intervalMinutes && (
                        <p className="text-text-muted">
                          Every {agent.scheduleConfig.intervalMinutes} minutes
                        </p>
                      )}
                      {agent.scheduleConfig.timezone && (
                        <p className="text-text-muted text-xs">
                          Timezone: {agent.scheduleConfig.timezone}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Trigger Condition */}
                {agent.triggerCondition && Object.keys(agent.triggerCondition).length > 0 && (
                  <div className="mt-3 p-3 bg-white rounded-md border border-warm-200">
                    <p className="text-xs font-medium text-text-secondary mb-2">Condition</p>
                    <pre className="text-xs font-mono text-text-muted overflow-x-auto">
                      {JSON.stringify(agent.triggerCondition, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Play className="w-4 h-4" />
            Actions
          </CardTitle>
          <CardDescription>
            {agent.actions.length} action{agent.actions.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {agent.actions.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <Settings2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No actions configured</p>
              <p className="text-sm mt-1">Add actions to define what this agent does.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {agent.actions.map((action, index) => {
                const actionConfig = ACTION_TYPE_CONFIG[action.type] || {
                  label: action.type,
                  icon: <Zap className="w-4 h-4" />,
                }
                return (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-warm-50 rounded-lg border border-border-subtle"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 border border-warm-200">
                      <span className="text-text-muted">{actionConfig.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-text">{actionConfig.label}</span>
                        <Badge variant="outline" className="text-xs">
                          Step {index + 1}
                        </Badge>
                      </div>
                      {action.promptId && (
                        <p className="text-xs text-text-muted mt-1">
                          Prompt ID: <span className="font-mono">{action.promptId}</span>
                        </p>
                      )}
                      {action.config && Object.keys(action.config).length > 0 && (
                        <pre className="mt-2 text-xs font-mono text-text-muted bg-white p-2 rounded border border-warm-200 overflow-x-auto">
                          {JSON.stringify(action.config, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-text-muted">Created</p>
              <p className="text-text">{new Date(agent.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-text-muted">Updated</p>
              <p className="text-text">{new Date(agent.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default AgentEditor
